import { config } from "../../../app-confi/app-config";
import { Point3 } from "../../../common/models/point.type";
import { ServiceManager } from "../../../services/service-manager";
import { alignment } from "../models/alignment.enum";
import { buildingType } from "../models/building-type.enum";
import { ColumnDefinition, inclusionEnum } from "../models/ColumnDefinition/column-definition";
import { columnDefs, columnSubtype, subtypeEnum, variantEnum } from "./models/column-definition";
import { columnType } from "./models/column-type.enum";
import { headType } from "./models/head-type.enum";
import { headTypesModel } from "./models/head-types.model";
import { PropsHybrid } from "./models/props-config";
import { segmentType } from "./models/segment-type";

export class HybridService {

    static getColumnBays(type: columnType) {
        if (type !== columnType.none) {
            const findMoosPlazaType = columnDefs.find(s => s.type === type);
            if (findMoosPlazaType) {
                return findMoosPlazaType.bays;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    static getHeadTypeAlignmentToBuilding(type: headType) {
        return headTypesModel.find(h => h.type === type).alignsWithBuilding;
    }

    static getHeadTypeBays(type: headType) {
        const findHeadType = columnDefs.find(s => s.type === type);
        return findHeadType.bays;
    }

    static getTotalBays(p: PropsHybrid) {
        const res = Math.ceil(p.buildingLength / config.hybridBaySize);
        return res;
    }

    static getTotalFloors(p: PropsHybrid) {
        return Math.ceil(p.buildingHeight / config.hybridFloorHeight);
    }

    static getTotalFloorsForElevator(p: PropsHybrid) {
        // crown is always treated as 1 floor when determining the need of elevators
        return p.plinthFloors + ServiceManager.buildingHelperService.getMiddleFloors(p.plinthType, p.buildingHeight, p.customPlinthHeight, p.crownFloors, p.plinthFloors, buildingType.hybrid) + 1;
    }

    static getCoresToOccupyWithElevators(p: PropsHybrid) {
        const extraElevators = HybridService.getExtraElevators(p);
        const elevatorsProvidedByMoosPlaza = 2;
        const elevatorsProvidedByLeftHead = p.headLeftType !== headType.none ? 2 : 0;
        const elevatorsProvidedByRightHead = p.headRightType !== headType.none ? 2 : 0;

        // how many cores we need to give to elevators OTLH2 types?
        const elevatorsProvidedByMoosPlazaAndHeads = elevatorsProvidedByMoosPlaza + elevatorsProvidedByLeftHead + elevatorsProvidedByRightHead;

        if (elevatorsProvidedByMoosPlazaAndHeads < extraElevators) {
            const elevatorsStillNeededInCores = extraElevators - elevatorsProvidedByMoosPlazaAndHeads;
            return Math.ceil(elevatorsStillNeededInCores / 2);
        } else {
            return 0;
        }
    }

    static getBodySizeInBaysWithoutMoosPleinAndHeads(p: PropsHybrid) {
        let bodySizeBays = HybridService.getTotalBays(p) - HybridService.getColumnBays(p.moosPlazaType);

        if (p.headLeftType !== headType.none) {
            bodySizeBays -= this.getHeadTypeBays(p.headLeftType);
        }
        if (p.headRightType !== headType.none) {
            bodySizeBays -= this.getHeadTypeBays(p.headRightType);
        }

        return bodySizeBays > 0 ? bodySizeBays : 0;
    }

    static getStaircaseCores(p: PropsHybrid) {
        const extraCores = HybridService.getExtraCoresBasedOnColumnAmounts(p);
        const coresToOccupyWithElevators = HybridService.getCoresToOccupyWithElevators(p);
        const staircases = extraCores - coresToOccupyWithElevators;
        return staircases > 0 ? staircases : 0;
    }

    static getElevatorCoresThatShouldBeAdded(p: PropsHybrid) {
        const extraCores = HybridService.getExtraCoresBasedOnColumnAmounts(p);
        const coresToOccupyWithElevators = HybridService.getCoresToOccupyWithElevators(p);
        return extraCores < coresToOccupyWithElevators ? extraCores : coresToOccupyWithElevators;
    }

    static getBodySizeForCalculation(p: PropsHybrid) {
        let bodySize = HybridService.getBodySizeInBaysWithoutMoosPleinAndHeads(p);

        // how many staircases / elevators we need to add determines this final body count as well for columns
        const staircaseCores = HybridService.getStaircaseCores(p);
        const coresToOccupyWithElevators = HybridService.getCoresToOccupyWithElevators(p);

        bodySize -= staircaseCores;
        bodySize -= coresToOccupyWithElevators * 2;
        return bodySize > 0 ? bodySize : 0;
    }

    static getBodySizeForComputationBasedOnColumnsAmount(p: PropsHybrid) {
        const colDef = columnDefs;

        return p.amount_G_XL_Columns * colDef.find(c => c.type === columnType.G_XL).bays +
            p.amount_G_M_Columns * colDef.find(c => c.type === columnType.G_M).bays +
            p.amount_G_S1S2_Columns * colDef.find(c => c.type === columnType.G_S1S2).bays +
            p.amount_G_L1L2_Columns * colDef.find(c => c.type === columnType.G_L1L2).bays;
    }

    static getExtraCoresBasedOnColumnAmounts(p: PropsHybrid) {
        const bodySize = HybridService.getBodySizeForComputationBasedOnColumnsAmount(p);
        const extraCores = p.moosPlazaAlignment === alignment.center ?
            Math.floor(bodySize * config.hybridBaySize / config.maxBodyLengthHybrid) - 1 :
            Math.floor(bodySize * config.hybridBaySize / config.maxBodyLengthHybrid);
        return extraCores > 0 ? extraCores : 0;
    }

    static getExtraElevators(p: PropsHybrid) {
        let minElevatorsNeededBasedOnHeight = 0;
        let minElevatorsNeededBasedOnWidth = 0;

        const maxNumberOfUnits = Math.floor(HybridService.getBodySizeInBaysWithoutMoosPleinAndHeads(p) / 3) * 2 * HybridService.getTotalFloors(p);
        minElevatorsNeededBasedOnWidth = Math.round(maxNumberOfUnits / 100);

        const floors = HybridService.getTotalFloorsForElevator(p);
        if (floors > 4 && floors <= 9) {
            minElevatorsNeededBasedOnHeight = 1;
        } else if (floors > 9) {
            minElevatorsNeededBasedOnHeight = 2;
        }

        // fix to reflect on units only if we're higher than 4 floors, up until then we can choose not to use them
        let res = 0;
        if (floors > 4) {
            res = minElevatorsNeededBasedOnHeight > minElevatorsNeededBasedOnWidth ? minElevatorsNeededBasedOnHeight : minElevatorsNeededBasedOnWidth;
        } else {
            res = minElevatorsNeededBasedOnHeight;
        }
        return res;
    }

    static initialDimensions(dimensions: Point3): boolean {
        return dimensions[0] === 1 && dimensions[1] === 1 && dimensions[2] === 1;
    }

    static getTotalPercentages(p: PropsHybrid): { totalPercentageG: number, totalPercentageH: number } {
        const gTypePercentages = p.unitWishList.filter(s => s.type.includes('G-')).map(s => s.percentage);
        const hTypePercentages = p.unitWishList.filter(s => s.type.includes('H-')).map(s => s.percentage);
        const totalPercentageG = gTypePercentages.reduce((a, b) => a + b, 0);
        const totalPercentageH = hTypePercentages.reduce((a, b) => a + b, 0);
        return { totalPercentageG, totalPercentageH };
    }

    static getAmountsByAvailableBays(p: PropsHybrid): {
        amount_G_M_Columns: number;
        amount_G_S1S2_Columns: number;
        amount_G_XL_Columns: number;
        amount_G_L1L2_Columns: number
    } {
        const colDef = columnDefs;
        const bays_G_M = colDef.find(c => c.type === columnType.G_M).bays;
        const bays_G_S1S2 = colDef.find(c => c.type === columnType.G_S1S2).bays;
        const bays_G_XL = colDef.find(c => c.type === columnType.G_XL).bays;
        const bays_G_L1L2 = colDef.find(c => c.type === columnType.G_L1L2).bays;

        let availableBays = HybridService.getBodySizeForCalculation(p);
        let amount_G_M = 0;
        let amount_G_S1S2 = 0;
        let amount_G_XL = 0;
        let amount_G_L1L2 = 0;

        let result = {} as any;

        if (availableBays > 0) {
            const removeBays = () => {
                availableBays -= bays_G_M;
                if (availableBays > 0) {
                    amount_G_M++;
                    availableBays -= bays_G_S1S2;
                    if (availableBays > 0) {
                        amount_G_S1S2++;
                        availableBays -= bays_G_XL;
                        if (availableBays > 0) {
                            amount_G_XL++;
                            availableBays -= bays_G_L1L2;
                            if (availableBays > 0) {
                                amount_G_L1L2++;
                                removeBays();
                            }
                        }
                    }
                }
            }
            removeBays();
            result.amount_G_M_Columns = amount_G_M;
            result.amount_G_S1S2_Columns = amount_G_S1S2;
            result.amount_G_XL_Columns = amount_G_XL;
            result.amount_G_L1L2_Columns = amount_G_L1L2;
        } else {
            result.amount_G_M_Columns = 0;
            result.amount_G_S1S2_Columns = 0;
            result.amount_G_XL_Columns = 0;
            result.amount_G_L1L2_Columns = 0;
        }
        return result;
    }

}